/** @file node.reparent: moves a node under another parent, optionally keeping its rest world point. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { Command } from '../types/command';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { orderNodes } from './orderNodes';
import { requireNode } from './requireNode';
import { restWorld } from './restWorld';
import { subtreeOf } from './subtreeOf';

/** True when reparenting `name` back to `parent` yields the key order `before` (sibling order can shift). */
function restoresOrder(nodes: Record<string, PuppetNode>, name: string, parent: string, before: string[]): boolean {
  const back = structuredClone(nodes);
  const node = back[name];
  if (node !== undefined) node.parent = parent;
  return Object.keys(orderNodes(back)).join('/') === before.join('/');
}

/**
 * `node.reparent { name, parent, keepWorld? }` → `{}`: `NODE_CYCLE` when `parent` is the node or below it;
 * `keepWorld` recomputes `pos` from the rest world points. Inverse: reparent back, then restore `pos` if it moved;
 * P when reparenting back would not restore the node key order.
 */
export const nodeReparentCommand: CommandDef<'node.reparent'> = {
  name: 'node.reparent',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'parent', 'keepWorld']);
    return compact({ name: r.str('name'), parent: r.str('parent'), keepWorld: r.opt('keepWorld', r.bool) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const node = requireNode(p, input.name, '/input/name');
    if (!Object.hasOwn(p.nodes, input.parent)) throwError(ERROR_CODES.NODE_PARENT_UNKNOWN, { parent: input.parent }, { path: '/input/parent' });
    if (subtreeOf(p, input.name).includes(input.parent) || node.parent === undefined) {
      throwError(ERROR_CODES.NODE_CYCLE, { node: input.name, parent: input.parent }, { path: '/input/parent', hint: 'nodes form a tree' });
    }
    const fallback = puppetInverse(draft);
    const before = Object.keys(p.nodes);
    const inverse: Command[] = [{ name: 'node.reparent', input: { name: input.name, parent: node.parent } }];
    if (input.keepWorld === true) {
      const [w, q] = [restWorld(p, input.name), restWorld(p, input.parent)];
      inverse.push({ name: 'node.set', input: { name: input.name, fields: { pos: [...node.pos] } } });
      node.pos = [w[0] - q[0], w[1] - q[1], w[2] - q[2]];
    }
    const prevParent = node.parent;
    node.parent = input.parent;
    p.nodes = orderNodes(p.nodes);
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse: restoresOrder(p.nodes, input.name, prevParent, before) ? inverse : fallback };
  },
};
